import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { Button, EmptyState, Skeleton } from '../components/ui'
import { useSystems } from '../api/hooks'
import { MappingWorkbench } from '../components/features/MappingWorkbench'

/**
 * The mapping wizard as its own page (`/systems/:uid/mapping`): the
 * SystemDetail Mapping card keeps the per-kind summary, its Workbench button
 * navigates here. MappingWorkbench renders the ordered, gated wizard (Groups
 * → Devices → Doors → Access groups → Credentials → People); its own "Back
 * to summary" closes back to the system detail page.
 */
export default function SystemMappingPage() {
  const { uid } = useParams<{ uid: string }>()
  const navigate = useNavigate()

  const systems = useSystems()
  const system = systems.data?.find((s) => s.unique_id === uid)

  if (systems.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-56" />
      </div>
    )
  }

  if (systems.isError) {
    return <EmptyState title="Could not load systems" hint={systems.error.message} />
  }

  if (!system) {
    return (
      <EmptyState
        title="System not found"
        hint={`No system with id ${uid ?? ''} on this customer.`}
        action={
          <Button variant="ghost" onClick={() => navigate('/systems')}>
            ← Back to systems
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Connections" title={`Mapping — ${system.name}`}>
        <p className="tnum mt-1 text-xs text-ostrel-ink-muted">
          <Link to="/systems" className="hover:text-ostrel-gold">
            Systems
          </Link>
          {' / '}
          <Link to={`/systems/${system.unique_id}`} className="hover:text-ostrel-gold">
            {system.name}
          </Link>
          {' / '}
          Mapping
        </p>
      </PageHeader>

      <MappingWorkbench system={system} onClose={() => navigate(`/systems/${system.unique_id}`)} />
    </div>
  )
}
